import { For, Show, createMemo, createSignal } from "solid-js";

import { useTagSuggestions } from "~/lib/hooks";

type Props = {
  wsKey: () => string;
  entityType?: "account" | "contact" | "deal" | "all";
  value: () => string[];
  onChange: (next: string[]) => void;
  // Optional per-tag max length (matches backend ≤64).
  maxTagLength?: number;
};

const DEFAULT_MAX_LEN = 64;

function dedupe(values: string[]): string[] {
  const seen = new Map<string, string>();
  for (const raw of values) {
    const t = raw.trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (!seen.has(k)) seen.set(k, t);
  }
  return Array.from(seen.values());
}

export function TagsEditor(props: Props) {
  const [draft, setDraft] = createSignal("");
  const [open, setOpen] = createSignal(false);

  const tagsLower = createMemo(() => new Set(props.value().map((t) => t.toLowerCase())));

  const suggestions = useTagSuggestions(
    props.wsKey,
    () => draft(),
    () => props.entityType ?? "all",
  );

  const visibleSuggestions = createMemo(() =>
    (suggestions.data?.items ?? []).filter((t) => !tagsLower().has(t.toLowerCase())),
  );

  const addTag = (raw: string) => {
    const t = raw.trim().slice(0, props.maxTagLength ?? DEFAULT_MAX_LEN);
    if (!t) return;
    if (tagsLower().has(t.toLowerCase())) return;
    props.onChange(dedupe([...props.value(), t]));
    setDraft("");
  };

  const removeTag = (tag: string) => {
    props.onChange(props.value().filter((t) => t !== tag));
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (draft().trim()) addTag(draft());
      return;
    }
    if (e.key === "Backspace" && !draft() && props.value().length > 0) {
      // Quick remove the last tag.
      removeTag(props.value()[props.value().length - 1]);
    }
  };

  return (
    <div class="relative">
      <div class="flex flex-wrap gap-1 input input-bordered min-h-[2.5rem] py-1.5 items-center">
        <For each={props.value()}>
          {(tag) => (
            <span class="badge badge-neutral gap-1 pl-2">
              {tag}
              <button
                type="button"
                class="btn btn-ghost btn-xs btn-circle"
                aria-label={`Remove tag ${tag}`}
                onClick={() => removeTag(tag)}
              >
                ✕
              </button>
            </span>
          )}
        </For>
        <input
          type="text"
          class="flex-1 min-w-[8rem] bg-transparent outline-none text-sm"
          placeholder={props.value().length === 0 ? "Add tags…" : ""}
          aria-label="Add tag"
          value={draft()}
          maxlength={props.maxTagLength ?? DEFAULT_MAX_LEN}
          onInput={(e) => {
            setDraft(e.currentTarget.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={handleKeyDown}
          autocomplete="off"
        />
      </div>
      <Show when={open() && (visibleSuggestions().length > 0 || draft().length > 0)}>
        <ul
          role="listbox"
          aria-label="Tag suggestions"
          class="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg bg-base-100 shadow-lg border border-base-300"
        >
          <For each={visibleSuggestions()}>
            {(s) => (
              <li>
                <button
                  type="button"
                  class="w-full text-left px-3 py-1.5 hover:bg-base-200 text-sm"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addTag(s)}
                >
                  {s}
                </button>
              </li>
            )}
          </For>
          <Show
            when={
              draft().trim() &&
              !visibleSuggestions().some((s) => s.toLowerCase() === draft().trim().toLowerCase()) &&
              !tagsLower().has(draft().trim().toLowerCase())
            }
          >
            <li>
              <button
                type="button"
                class="w-full text-left px-3 py-1.5 hover:bg-base-200 text-sm italic opacity-80"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addTag(draft())}
              >
                Create "{draft().trim()}"
              </button>
            </li>
          </Show>
        </ul>
      </Show>
    </div>
  );
}

export function TagBadges(props: { tags: () => string[] | null | undefined; size?: "xs" | "sm" }) {
  const size = props.size ?? "sm";
  return (
    <div class="flex flex-wrap gap-1">
      <For each={props.tags() ?? []}>
        {(tag) => <span class={`badge badge-${size} badge-outline`}>{tag}</span>}
      </For>
    </div>
  );
}
